import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { FileDownload as CsvIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, timeAbsenceRequestAPI } from '../services/api';
import { TimezoneService } from '../services/timezoneService';

interface TimeOffReportFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  type: string;
  status: string;
  page: number;
  limit: number;
}

interface TimeOffRequestRecord {
  id: string;
  type: string;
  status: string;
  startDate: string;
  endDate: string;
  reason?: string;
  createdAt: string;
  user: {
    id: string;
    employeeId: string;
    firstName: string;
    lastName: string;
    timezone?: string;
  };
}

const REQUEST_TYPES = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'personal', label: 'Personal' },
  { value: 'sick', label: 'Sick' },
  { value: 'other', label: 'Other' },
];

const REQUEST_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ALLOWED_ROLES = ['supervisor', 'manager', 'administrator', 'super_user'];

function formatRequestType(type: string): string {
  return REQUEST_TYPES.find((item) => item.value === type)?.label
    || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}

function getStatusLabel(status: string): string {
  return REQUEST_STATUSES.find((item) => item.value === status)?.label
    || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
}

function getStatusColor(status: string): 'default' | 'warning' | 'success' | 'error' | 'info' {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
}

function formatDate(dateString: string, employeeTimezone?: string, viewerTimezone?: string): string {
  if (!dateString) return '';

  const timezone = employeeTimezone || viewerTimezone || TimezoneService.getUserTimezone() || 'UTC';

  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return moment.tz(dateString, 'YYYY-MM-DD', timezone).format('MMM D, YYYY');
  }

  return moment(dateString).tz(timezone).format('MMM D, YYYY');
}

function calculateDuration(startDate: string, endDate: string): number {
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  return end.diff(start, 'days') + 1;
}

const TimeOffRequestsReportPage: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<TimeOffReportFilters>({
    startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
    endDate: moment().add(2, 'months').format('YYYY-MM-DD'),
    userId: '',
    departmentId: '',
    type: '',
    status: '',
    page: 1,
    limit: 50,
  });
  const [showFilters, setShowFilters] = useState(true);
  const [sortBy, setSortBy] = useState<'employee' | 'type' | 'start' | 'end' | 'status' | 'created'>('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const canAccess = ALLOWED_ROLES.includes(user?.role || '');

  const { data: reportData, isLoading, error, refetch } = useQuery({
    queryKey: ['timeOffRequestsReport', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      });
      const response = await timeAbsenceRequestAPI.getReport(params);
      return response.data;
    },
    enabled: canAccess,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.users.getAll(),
    enabled: canAccess,
  });

  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => apiClient.departments.getAll(),
    enabled: canAccess,
  });

  const handleFilterChange = (field: keyof TimeOffReportFilters, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      endDate: moment().add(2, 'months').format('YYYY-MM-DD'),
      userId: '',
      departmentId: '',
      type: '',
      status: '',
      page: 1,
      limit: 50,
    });
  };

  const toggleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDir('asc');
  };

  const sortedRequests = useMemo(() => {
    const list: TimeOffRequestRecord[] = reportData?.data ? [...reportData.data] : [];
    list.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortBy) {
        case 'employee':
          av = `${a.user?.lastName || ''} ${a.user?.firstName || ''}`;
          bv = `${b.user?.lastName || ''} ${b.user?.firstName || ''}`;
          break;
        case 'type':
          av = a.type;
          bv = b.type;
          break;
        case 'start':
          av = a.startDate;
          bv = b.startDate;
          break;
        case 'end':
          av = a.endDate;
          bv = b.endDate;
          break;
        case 'status':
          av = a.status;
          bv = b.status;
          break;
        case 'created':
        default:
          av = a.createdAt;
          bv = b.createdAt;
          break;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [reportData?.data, sortBy, sortDir]);

  const handleExportCsv = async () => {
    const params: Record<string, string | number> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && key !== 'page' && key !== 'limit') {
        params[key] = value;
      }
    });

    let allRecords: TimeOffRequestRecord[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await timeAbsenceRequestAPI.getReport({ ...params, page, limit: 500 });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;
      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    const headers = ['Employee', 'Employee ID', 'Type', 'Start Date', 'End Date', 'Days', 'Status', 'Submitted'];
    const rows = allRecords.map((record) => [
      `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.trim(),
      record.user?.employeeId || '',
      formatRequestType(record.type),
      formatDate(record.startDate, record.user?.timezone, user?.timezone),
      formatDate(record.endDate, record.user?.timezone, user?.timezone),
      String(calculateDuration(record.startDate, record.endDate)),
      getStatusLabel(record.status),
      TimezoneService.formatDateTimeConsistent(record.createdAt, record.user?.timezone || 'UTC'),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `time-off-requests-${filters.startDate}-to-${filters.endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!canAccess) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Time Off Requests Report
        </Typography>
        <Alert severity="warning">
          Access denied. Only supervisors, managers, administrators, and super users can access this report.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Time Off Requests Report
        </Typography>
        <Button variant="outlined" onClick={() => setShowFilters(!showFilters)}>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </Box>

      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={usersData?.data?.data || []}
                  value={filters.userId
                    ? usersData?.data?.data?.find((item: any) => item.id === filters.userId) || null
                    : null}
                  onChange={(_event, newValue) => handleFilterChange('userId', newValue?.id || '')}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.employeeId})`}
                  renderInput={(params) => <TextField {...params} label="Employee" placeholder="Search employee..." />}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No employees found"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Department"
                  value={filters.departmentId}
                  onChange={(e) => handleFilterChange('departmentId', e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {(departmentsData?.data?.data || []).map((department: any) => (
                    <MenuItem key={department.id} value={department.id}>
                      {department.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {REQUEST_TYPES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {REQUEST_STATUSES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={() => refetch()}>
                Apply Filters
              </Button>
              <Button variant="outlined" onClick={handleResetFilters}>
                Reset
              </Button>
              <Button
                variant="outlined"
                startIcon={<CsvIcon />}
                onClick={handleExportCsv}
                disabled={!reportData?.data || reportData.data.length === 0}
              >
                Export CSV
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load time off requests. Please try again later.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Time Off Requests
            {reportData?.pagination && (
              <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                ({reportData.pagination.total} total records)
              </Typography>
            )}
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('employee')}>
                        Employee {sortBy === 'employee' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell>Employee ID</TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('type')}>
                        Type {sortBy === 'type' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('start')}>
                        Start Date {sortBy === 'start' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('end')}>
                        End Date {sortBy === 'end' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>
                        Status {sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer' }} onClick={() => toggleSort('created')}>
                        Submitted {sortBy === 'created' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRequests.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          {record.user?.firstName} {record.user?.lastName}
                        </TableCell>
                        <TableCell>{record.user?.employeeId}</TableCell>
                        <TableCell>
                          <Chip label={formatRequestType(record.type)} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{formatDate(record.startDate, record.user?.timezone, user?.timezone)}</TableCell>
                        <TableCell>{formatDate(record.endDate, record.user?.timezone, user?.timezone)}</TableCell>
                        <TableCell>{calculateDuration(record.startDate, record.endDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(record.status)}
                            color={getStatusColor(record.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {TimezoneService.formatDateTimeConsistent(record.createdAt, record.user?.timezone || 'UTC')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {sortedRequests.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No time off requests found for the selected filters.
                  </Typography>
                </Box>
              )}

              {reportData?.pagination && reportData.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={reportData.pagination.totalPages}
                    page={filters.page}
                    onChange={(_event, value) => handleFilterChange('page', value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TimeOffRequestsReportPage;
